import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import {
  DASHBOARD_APP_BASE,
  SUPPORTED_DASHBOARDS,
} from "src/layouts/HomePage/SidebarManagement/DashboardRoutes";
import { NotificationProvider } from "../contexts/NotificationContext";
import { MenuProvider } from "../contexts/MenuContext";
import SignUp from "../layouts/Authentication/SignUp";
import Otp from "../layouts/Authentication/Otp";
import EmailSent from "../layouts/Authentication/ResetPasswordNotification";
import LoginIn from "../layouts/Authentication/LogIn";
import ResetPasswordEmail from "../layouts/Authentication/ForgotPasswordEmail";
import MakeNewPassword from "../layouts/Authentication/MakeNewPassword";
import Sidebar from "../layouts/OnboardingSidebar/OnboardingSidebar";
import MagicScreen from "../layouts/MagicScreen";
import PrivacyPolicy from "../layouts/PrivacyPolicy/PrivacyPolicy";
import HealthCheckPage from "../components/miscellaneous/health-check";
import Chatbot from "../layouts/AskDatatram/ChatLayout";
import Dashboard from "../layouts/HomePage/SidebarManagement/HomepageLayout";
import SourceConnector from "../layouts/SourceConnector/NewSourceConnectorScreen";
import UserManagement from "../layouts/UserManagement/UserManagement";
import AccountSettings from "../layouts/MyAccount/Settings/AccountSettings";
import NotificationPreferences from "../layouts/MyAccount/Notifications/NotificationPreferences";
import AllNotifications from "../layouts/MyAccount/Notifications/AllNotifications";
import DashboardPage from "../components/TableauDashboard/renderDashboards";
import UserIntro from "../layouts/ProfileSetup/UserIntro";
import SessionChecker from "src/components/SessionChecker";
import ProtectedRoute from "./ProtectedRoute";
import RequireDashboardAuth from "./RequireDashboardAuth";
import AIChatScreen from "src/layouts/AskDatatram/ChatScreen";
import HelpSupport from "src/layouts/HelpSupport/HelpSupport";
import { PaidMediaDashboard } from "src/dashboards/paidMedia";

/** Old `/account/dashboards/...` → `/dashboard/...` */
const LegacyAccountDashboardsFolderRedirect: React.FC = () => {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/account\/dashboards\/?/, "");
  const to = suffix
    ? `${DASHBOARD_APP_BASE}/${suffix.replace(/^\/+/, "")}`
    : DASHBOARD_APP_BASE;
  return <Navigate to={to} replace state={location.state} />;
};

const UserRoutes: React.FC = () => (
  <NotificationProvider>
    <MenuProvider>
      <SessionChecker />
      <Routes>
        {/* ======== Routes for Datatram's Authentication =======*/}
        <Route path="/" element={<Navigate to="/log-in" />} />
        <Route path="/sign-up" element={<SignUp />} />
        <Route
          path="/verify-otp"
          element={
            <ProtectedRoute allowedFrom="sign-up" redirectTo="/log-in">
              <Otp />
            </ProtectedRoute>
          }
        />
        <Route path="/log-in" element={<LoginIn />} />
        {/* ======== Routes for Reset/Forget Passwords =======*/}
        <Route
          path="/forgot-password/confirmation"
          element={
            <ProtectedRoute allowedFrom="forgot-password" redirectTo="/log-in">
              <EmailSent />
            </ProtectedRoute>
          }
        />
        <Route path="/forgot-password" element={<ResetPasswordEmail />} />
        <Route path="/reset-password" element={<MakeNewPassword />} />
        {/* ======= Routes for Static / Miscellaneous Pages ======*/}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/health" element={<HealthCheckPage />} />
        <Route path="/ask-datatram" element={<Chatbot />} />
        <Route
          path="/account/dashboards/product-analytics-dashboard"
          element={
            <Navigate to="/dashboard/sales-product-analytics-dashboard" replace />
          }
        />
        <Route
          path="/account/dashboards/marketing-analytics-dashboard"
          element={<Navigate to="/dashboard/web-analytics-dashboard" replace />}
        />
        <Route
          path="/account/dashboards/meta-ads-overview"
          element={<Navigate to="/dashboard/paid-media-dashboard" replace />}
        />
        <Route
          path="/paid-media"
          element={<Navigate to="/dashboard/paid-media-dashboard" replace />}
        />
        <Route
          path="/paid-media/meta-ads-report"
          element={
            <Navigate
              to="/dashboard/paid-media-dashboard/meta-ads-report"
              replace
            />
          }
        />
        <Route
          path="/account/paid-media-dashboard/meta-ads-report"
          element={
            <Navigate
              to="/dashboard/paid-media-dashboard/meta-ads-report"
              replace
            />
          }
        />
        <Route
          path="/account/dashboards"
          element={<Navigate to={DASHBOARD_APP_BASE} replace />}
        />
        <Route
          path="/account/dashboards/*"
          element={<LegacyAccountDashboardsFolderRedirect />}
        />
        {SUPPORTED_DASHBOARDS.map(d => {
          const slug = d.route.replace(
            new RegExp(`^${DASHBOARD_APP_BASE}/`),
            "",
          );
          return (
            <Route
              key={d.dashboardId}
              path={`/account/${slug}`}
              element={<Navigate to={d.route} replace />}
            />
          );
        })}
        {/* ======== Routes for Magic Screen =======*/}
        <Route
          path="/welcome-to-datatram/data-loading"
          element={<MagicScreen />}
        />
        {/* ======== Routes for Onboarding Screens =======*/}
        <Route path="/welcome-to-datatram" element={<Sidebar />}>
          <Route
            path="basic-information"
            element={
              <ProtectedRoute allowedFrom="welcome" redirectTo="/log-in">
                <UserIntro />
              </ProtectedRoute>
            }
          />
          <Route
            path="connect-sources"
            element={
              <ProtectedRoute
                allowedFrom="basic-information"
                redirectTo="/log-in"
              >
                <SourceConnector />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="/dashboard" element={<Dashboard />}>
          <Route
            index
            element={
              <RequireDashboardAuth>
                <DashboardPage />
              </RequireDashboardAuth>
            }
          />
          <Route
            path="paid-media-dashboard/meta-ads-report"
            element={
              <RequireDashboardAuth>
                <PaidMediaDashboard />
              </RequireDashboardAuth>
            }
          />
          <Route
            path=":dashboardSlug"
            element={
              <RequireDashboardAuth>
                <DashboardPage />
              </RequireDashboardAuth>
            }
          />
        </Route>

        <Route path="/account" element={<Dashboard />}>
          <Route index element={<Navigate to={DASHBOARD_APP_BASE} replace />} />
          <Route
            path="/account/connect-sources"
            element={<SourceConnector />}
          />
          <Route path="user-management" element={<UserManagement />} />
          <Route path="help-and-support" element={<HelpSupport />} />
          <Route path="sources" element={<SourceConnector />} />
          <Route path="account-setting" element={<AccountSettings />} />

          {/* My Account */}
          <Route
            path="my-account"
            element={<Navigate to="/account/my-account/account-settings" replace />}
          />
          <Route
            path="my-account/account-settings"
            element={<AccountSettings />}
          />
          <Route
            path="my-account/notification-preferences"
            element={<NotificationPreferences />}
          />
          <Route
            path="my-account/all-notifications"
            element={<AllNotifications />}
          />
          <Route
            path="my-account/*"
            element={<Navigate to="/account/my-account/account-settings" replace />}
          />

          <Route path="ai-chat" element={<AIChatScreen />} />
          <Route
            path="engagement/*"
            element={<Navigate to={DASHBOARD_APP_BASE} replace />}
          />
          <Route path="review-and-send" element={<Navigate to={DASHBOARD_APP_BASE} replace />} />
          <Route path="ai-review-and-send" element={<Navigate to="/account/ai-chat" replace />} />
          <Route path="social-listing-agent/*" element={<Navigate to={DASHBOARD_APP_BASE} replace />} />
        </Route>
      </Routes>
    </MenuProvider>
  </NotificationProvider>
);

export default UserRoutes;
